/**
 * deriveWizardSummaryState — pure status derivation for SummaryStep.
 *
 * Design principle:
 *   When `onboardingState` is available (non-degraded mode), the plugin bridge
 *   response is the SINGLE source of truth for readiness and indexing progress.
 *   The `indexingFromHook` / `operatorStateFallback` values from gateway-API
 *   hooks are only consulted in degraded mode, except for a local initial
 *   admission failure belonging to the same queued job with no usable index.
 *
 * This removes false "store not ready" states caused by slow or lagging gateway
 * API calls when the bridge already reports can_search=true / readiness=true.
 *
 * readiness.can_search hierarchy:
 *   1. If `onboardingState.readiness.can_search` is present → use it as the
 *      primary readiness signal (plus active_index_present for safety).
 *   2. If `readiness` sub-object is absent (older bridge response) → derive
 *      readiness from `indexing.active_index_present` + `indexing.status`.
 *   3. If `onboardingState` is null (bridge has not replied yet) → storeReady=false.
 *   4. If `isDegraded` (plugin too old for onboarding.state) → fall back to
 *      `operatorStateFallback` + `indexingFromHook` gateway-lane data.
 *
 * @param {object}        opts
 * @param {object|null}   opts.onboardingState         — bridge onboarding.state payload
 * @param {boolean}       opts.isDegraded               — plugin is too old
 * @param {object}        opts.operatorStateFallback    — { connection, indexing } from useOperatorState
 * @param {object}        opts.indexingFromHook
 *   { indexingStatus, indexingProgress, activeIndexPresent, error } from useIndexingProgress
 * @param {string}        opts.buildStatus              — local build job state machine
 * @param {Error|null}    opts.buildError
 * @param {Error|null}    opts.bridgeError
 * @returns {object}
 */

import { ConnectionStatus, IndexingStatus } from '../../../home/operatorState';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function normalizeIndexingStatus(rawStatus) {
  if (rawStatus === 'done' || rawStatus === 'completed') return IndexingStatus.READY;
  if (rawStatus === 'running' || rawStatus === 'queued') return IndexingStatus.RUNNING;
  if (rawStatus === 'error' || rawStatus === 'failed') return IndexingStatus.ERROR;
  return IndexingStatus.IDLE;
}

function isBuildPending(status) {
  return status === 'queued' || status === 'running';
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function deriveWizardSummaryState({
  onboardingState,
  isDegraded,
  operatorStateFallback,
  indexingFromHook,
  buildStatus,
  buildError,
  bridgeError,
}) {
  // ── Bridge-side onboarding fields (meaningful when not degraded) ──────────
  const wizardCompleted = onboardingState?.onboarding?.wizard_completed === true;
  const previewBuilt = onboardingState?.onboarding?.preview_built === true;
  const priorCheckpointsMet = onboardingState?.onboarding?.mode_selected === true
    && onboardingState?.onboarding?.integration_step_completed === true;
  const storedBuildJobId = onboardingState?.onboarding?.preview_build_job_id || null;
  const hasBridgeFailure = !!bridgeError && !onboardingState;

  // ── Mode-specific derivation ─────────────────────────────────────────────
  let connectionReady;
  let connectionError;
  let storeReady;
  let indexingStatusForUI;
  let indexingProgressForUI;
  let indexingError;

  if (isDegraded) {
    // ── Degraded: fall back to gateway-lane hook data ──────────────────────
    connectionReady = operatorStateFallback.connection === ConnectionStatus.CONNECTED;
    connectionError = operatorStateFallback.connection === ConnectionStatus.ERROR;
    storeReady = connectionReady && operatorStateFallback.indexing === IndexingStatus.READY;
    indexingStatusForUI = indexingFromHook.indexingStatus;
    indexingProgressForUI = indexingFromHook.indexingProgress;
    indexingError = indexingFromHook.error;
  } else if (onboardingState) {
    // ── Non-degraded with bridge data: bridge is the single source ─────────
    connectionReady = onboardingState.connection?.connected === true;
    connectionError = onboardingState.connection?.status === ConnectionStatus.ERROR;
    indexingError = onboardingState.indexing?.error || null;

    const bridgeIndexStatus = normalizeIndexingStatus(onboardingState.indexing?.status);
    indexingStatusForUI = bridgeIndexStatus;
    indexingProgressForUI = typeof onboardingState.indexing?.progress === 'number'
      ? Math.max(0, Math.min(100, onboardingState.indexing.progress))
      : 0;

    if (onboardingState.readiness != null) {
      // Explicit readiness contract (bridge v2+): can_search is the authoritative
      // signal. The plugin reports can_search=true only after the index is usable,
      // so we trust it directly without also requiring active_index_present.
      // Requiring active_index_present here caused false "not ready" states
      // when can_search=true but the gateway API was lagging (10N AC4 fix).
      storeReady = onboardingState.readiness.can_search === true;
    } else {
      // Older bridge response without readiness sub-object: derive from indexing.
      storeReady = connectionReady
        && bridgeIndexStatus === IndexingStatus.READY
        && onboardingState.indexing?.active_index_present === true;
    }
    if (!storeReady
      && !onboardingState.indexing?.active_index_present
      && onboardingState.indexing?.status === 'queued'
      && indexingFromHook.indexingStatus === IndexingStatus.ERROR
      && indexingFromHook.rawIndexingStatus === 'queued'
      && indexingFromHook.jobId
      && indexingFromHook.jobId === onboardingState.indexing?.job_id
    ) {
      indexingError = indexingError || indexingFromHook.error;
    }
  } else {
    // ── Non-degraded but bridge has not replied yet ────────────────────────
    connectionReady = false;
    connectionError = false;
    storeReady = false;
    indexingStatusForUI = IndexingStatus.IDLE;
    indexingProgressForUI = 0;
    indexingError = null;
  }

  // ── Error state ──────────────────────────────────────────────────────────
  const bridgeIndexError = !isDegraded && onboardingState
    ? normalizeIndexingStatus(onboardingState.indexing?.status) === IndexingStatus.ERROR
    : false;
  const hookIndexError = isDegraded
    ? indexingFromHook.indexingStatus === IndexingStatus.ERROR
    : false;

  const hasError = connectionError
    || hasBridgeFailure
    || bridgeIndexError
    || hookIndexError
    || !!indexingError
    || !!buildError;

  // ── Composite flags ──────────────────────────────────────────────────────
  const isReady = isDegraded ? storeReady : (storeReady && previewBuilt);

  // isCheckingState: waiting for the very first bridge response (non-degraded).
  // Degraded spinner is rendered separately via the {isDegraded && !isReady} branch.
  const isCheckingState = !isDegraded && !onboardingState && !bridgeError;

  const isBuildingPreview = !isDegraded && storeReady && !previewBuilt && isBuildPending(buildStatus);

  return {
    wizardCompleted,
    connectionReady,
    connectionError,
    hasBridgeFailure,
    previewBuilt,
    priorCheckpointsMet,
    storedBuildJobId,
    storeReady,
    hasError,
    isReady,
    isCheckingState,
    isBuildingPreview,
    indexingStatusForUI,
    indexingProgressForUI,
    indexingError,
  };
}
